import { Button, Icon, Text } from "@chakra-ui/react";
import { useCallback } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { SwitchInput } from "src/components/Inputs/SwitchInput";
import {
  GetOfferConfigurationsByEnrollmentPeriodIdResponse,
  updateOfferConfiguration,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { FormTemplateDefaultCard } from "../components/FormTemplateDefaultCard";

interface School {
  id: string;
  name: string;
}

type OfferConfiguration = NonNullable<
  GetOfferConfigurationsByEnrollmentPeriodIdResponse["data"]
>[number];

export type Props = {
  index: number;
  offerConfiguration: OfferConfiguration;
  schools: School[];
  isDisabled?: boolean;
  onDelete: () => Promise<void>;
  onEditClick?: () => void;
  onRefetch: () => Promise<void>;
};

export const OfferConfigurationCard: React.FC<Props> = ({
  index,
  offerConfiguration,
  schools,
  isDisabled = false,
  onEditClick,
  onDelete,
  onRefetch,
}) => {
  const toast = useAvelaToast();

  const { confirm: confirmActiveChange, confirmationDialog: activateDialog } =
    useConfirmationDialog({
      header: "Activate rule?",
      body: (
        <Text>
          This will enable the automatic behavior for this rule immediately.
        </Text>
      ),
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Activate rule",
      },
      translate: true,
    });

  const {
    confirm: confirmInactiveChange,
    confirmationDialog: inactivateDialog,
  } = useConfirmationDialog({
    header: "Deactivate rule?",
    body: (
      <Text>
        This will stop the automatic behavior for this rule immediately.
      </Text>
    ),
    cancelButton: {
      label: "Cancel",
    },
    confirmButton: {
      label: "Deactivate rule",
    },
    translate: true,
  });

  const handleSubmit = useCallback(async () => {
    try {
      await updateOfferConfiguration({
        path: { id: offerConfiguration.id },
        body: {
          active: !offerConfiguration.active,
          configuration: offerConfiguration.configuration,
        },
      });
      toast({
        title: "Success",
        description: `Rule ${
          offerConfiguration.active ? "deactivated" : "activated"
        }`,
        status: "success",
        duration: 2000,
      });
      await onRefetch();
    } catch (e) {
      toast({
        title: "Something went wrong",
        description: "Failed to change rule status",
        status: "error",
        duration: 2000,
      });
    }
  }, [offerConfiguration, toast, onRefetch]);

  const handleActiveChangeClick = useCallback(async () => {
    if (offerConfiguration.active) {
      if (!(await confirmInactiveChange())) {
        return;
      }
    } else {
      if (!(await confirmActiveChange())) {
        return;
      }
    }

    handleSubmit();
  }, [
    confirmActiveChange,
    handleSubmit,
    confirmInactiveChange,
    offerConfiguration.active,
  ]);

  const displayName = offerConfiguration.name || "Offer Configuration";

  const displayDescription = offerConfiguration.description || "";

  const tagLabel =
    offerConfiguration.configuration_type === "CLEAR_LOWER_RANKED"
      ? "Clear lower ranked"
      : "Auto revoke";

  return (
    <>
      <FormTemplateDefaultCard
        key={`offer-configuration-${index}`}
        tag={
          <Text
            fontSize="sm"
            fontWeight="bold"
            color="gray.700"
            bg="white"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="md"
            px={2}
            py={1}
            display="inline-block"
            width="fit-content"
          >
            {tagLabel}
          </Text>
        }
        title={displayName}
        description={displayDescription}
        switches={
          <SwitchInput<boolean>
            checked={[true, "Active"]}
            unchecked={[false, "Inactive"]}
            value={offerConfiguration.active}
            onChange={handleActiveChangeClick}
            switchDirection="row"
          />
        }
        actionSwitch={
          <Button
            leftIcon={<Icon as={RiDeleteBin6Line} />}
            variant="outline"
            colorScheme="gray"
            size="sm"
            isDisabled={isDisabled}
            onClick={onDelete}
          >
            Delete
          </Button>
        }
        onEditClick={onEditClick}
      />
      {activateDialog}
      {inactivateDialog}
    </>
  );
};
